/**
 * MagicLinkLandingPage — the catch-all route target.
 *
 * URL shape: `https://pay.<rcm-vendor>.<tld>/<token>` per
 * MAGIC_LINK_AUTH_DESIGN.md §4.1, locked at v0.8.2 with the per-RCM-vendor
 * deployment topology. Examples: `pay.primrose.health/qwer-tyui-oeur`,
 * `pay.iris-health.com/abcd-efgh-ijkl`. The token sits at the URL path
 * root (no `/m/` prefix, no query params, no hash fragment).
 *
 * Flow:
 *   1. Router matches the path against the reserved-name allowlist.
 *      If reserved (e.g. /login, /pay, /healthz, /favicon.ico),
 *      the catch-all is bypassed — those have their own routes.
 *   2. If the leftover path matches the base32 token regex (12-16
 *      alphanumerics after dash-stripping), this component renders.
 *   3. On mount, the token is captured from `useParams` into component
 *      state. The backend is NOT called on mount. Per ledger #41,
 *      consumption happens only on POST submit — preview-bot crawlers
 *      (iMessage, Slack, WhatsApp) GET the URL before the patient does.
 *   4. The patient enters their DOB and submits. POST hits
 *      `/api/v1/auth/magic-link`.
 *   5. On 200, navigate to `response.redirect_path` via
 *      `history.replaceState` so the token is scrubbed from the URL.
 *   6. On 401, render the form with the generic error message and let
 *      the patient retry. Backend's 3-strike lockout caps the
 *      re-attempt count server-side.
 *
 * Decision ledger touch points:
 *   #40 — catch-all + reserved allowlist
 *   #41 — no token consumption on GET
 *   #42 — trust redirect_path; do not decode JWT
 *   #43 — Phase A cutover: /login is the magic-link manual entry,
 *         old PIN+DOB form is unreachable when MAGIC_LINK_ENABLED
 *   #44 — only routed when MAGIC_LINK_ENABLED is true
 */
import { useNavigate, useParams } from 'react-router-dom';

import { useAuth } from '../../auth/AuthContext';
import type { MagicLinkResponse } from '../../api/types';
import { MagicLinkDobForm } from './MagicLinkDobForm';
import { LOGIN_THEME, CURRENT_BRAND } from '../../lib/loginTheme';
import { PrimroseLoginShell } from './shells/PrimroseLoginShell';
import { IrisLoginShell } from './shells/IrisLoginShell';

// Pick the brand shell once at module load — VITE_LOGIN_THEME is a
// build-time constant so this never changes at runtime.
const LoginShell = LOGIN_THEME === 'iris' ? IrisLoginShell : PrimroseLoginShell;

/**
 * The token's base32 alphabet excludes 0/o/1/i/l per design §4.1, but
 * we accept any alphanumeric (12-16 chars after dash-stripping) here
 * because the backend normalizes and is authoritative. Strict
 * client-side alphabet enforcement would reject patient-typo cases
 * that backend would handle gracefully.
 *
 * Length window: 12-16 chars dash-stripped. 12 is the design's
 * canonical (3 groups of 4); the upper bound of 16 gives headroom
 * for any future format expansion without re-deploying the FE.
 */
const TOKEN_PATTERN = /^[A-Za-z0-9-]+$/;

function isPlausibleToken(raw: string | undefined): raw is string {
  if (!raw) return false;
  if (!TOKEN_PATTERN.test(raw)) return false;
  const stripped = raw.replace(/-/g, '');
  return stripped.length >= 12 && stripped.length <= 16;
}

export function MagicLinkLandingPage() {
  const navigate = useNavigate();
  const auth = useAuth();
  // The catch-all route uses `:token` as the param name. See AppRouter.tsx.
  const { token } = useParams<{ token: string }>();

  // Defensive: if the URL slipped past the router pattern (e.g. someone
  // crafted a path that doesn't look like a token at all), fall back to
  // the manual login page rather than rendering an empty token input.
  // The router should catch this case, but belt-and-suspenders.
  if (!isPlausibleToken(token)) {
    return (
      <LoginShell>
        <div className="space-y-4">
          <div>
            <h2 className="text-lg font-bold">That link didn&rsquo;t look right</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Please use the link from your text message, or enter your
              code on the sign-in page.
            </p>
          </div>
          <button
            type="button"
            className="text-sm font-medium text-primary underline-offset-2 hover:underline"
            onClick={() => navigate('/login', { replace: true })}
          >
            Go to sign-in
          </button>
        </div>
      </LoginShell>
    );
  }

  const onSuccess = (response: MagicLinkResponse) => {
    auth.refresh();
    // Scrub the token from the URL via replace (not push) so the back
    // button doesn't return the patient to the token URL. Per ledger
    // #39 + the design §8.3 guidance to use redirect_path for the
    // destination.
    navigate(response.redirect_path, { replace: true });
  };

  return (
    <LoginShell>
      <div className={LOGIN_THEME === 'iris' ? 'ir-explainer' : 'pr-explainer'}>
        <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6">
          <circle cx="10" cy="10" r="8" />
          <path d="M10 9v4M10 6.5v.4" />
        </svg>
        <span>To protect your privacy, please enter your date of birth. We’ll display your bill only if it matches your provider’s records.</span>
      </div>

      <MagicLinkDobForm
        initialToken={token}
        hideTokenInput
        onSuccess={onSuccess}
      />

      <p className={LOGIN_THEME === 'iris' ? 'ir-help' : 'pr-help'}>
        Can&rsquo;t find your PIN? Call billing at <a href={`tel:${CURRENT_BRAND.billingPhoneTel}`}>{CURRENT_BRAND.billingPhoneDisplay}</a>.
      </p>
    </LoginShell>
  );
}

